import {DEFAULT_LOCAL_HOST} from '../constants/request';
import {isBrowserRuntime} from '../runtime/browser';

let cachedDefaultOrigin: string | undefined;

// Reset the cached default origin
export const resetDefaultOriginCache = (): void => {
  cachedDefaultOrigin = undefined;
};

/** Determines the base origin for relative URLs: browser location > VERCEL_URL > localhost:3000. */
export const resolveDefaultOrigin = (): string => {
  if (cachedDefaultOrigin) {
    return cachedDefaultOrigin;
  }

  if (isBrowserRuntime() && window.location?.origin) {
    cachedDefaultOrigin = window.location.origin;

    return cachedDefaultOrigin;
  }

  const vercelHost =
    process.env.VERCEL_PROJECT_PRODUCTION_URL ||
    process.env.VERCEL_BRANCH_URL ||
    process.env.VERCEL_URL;

  if (vercelHost) {
    cachedDefaultOrigin = `https://${vercelHost}`;

    return cachedDefaultOrigin;
  }

  cachedDefaultOrigin = DEFAULT_LOCAL_HOST;

  return cachedDefaultOrigin;
};

const isLocalHostname = (hostname: string): boolean => {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.endsWith('.localhost')
  );
};

/**
 * Keeps public `.test` origins for real deployed environments, but rewrites them
 * to the local internal origin when a built local server is handling the request.
 */
export const resolveInternalOrigin = (origin: string): string => {
  if (isBrowserRuntime() || process.env.VERCEL_URL) {
    return origin;
  }

  const defaultOrigin = resolveDefaultOrigin();

  try {
    const requestUrl = new URL(origin);
    const defaultUrl = new URL(defaultOrigin);

    if (
      requestUrl.hostname.endsWith('.test') &&
      isLocalHostname(defaultUrl.hostname)
    ) {
      return defaultOrigin;
    }
  } catch {
    return origin;
  }

  return origin;
};

/** Parses a URL string into a `URL` object, using {@link resolveDefaultOrigin} as base for relative paths. */
export const generateValidUrl = (url: string): URL => {
  return new URL(url, resolveDefaultOrigin());
};

/** Appends search params to a URL. Array values are expanded as `key[]=v1&key[]=v2`. */
export const formatURLSearchParams = (
  url: string,
  searchParams: Record<string, string | string[]>,
): string => {
  const nextUrl = generateValidUrl(url);

  for (const [key, value] of Object.entries(searchParams)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        nextUrl.searchParams.append(`${key}[]`, item);
      }

      continue;
    }

    nextUrl.searchParams.set(key, value);
  }

  return nextUrl.toString();
};

export const combinePathSegments = (
  basePathname: string,
  pathname: string,
): string => {
  const normalizedBase = basePathname.endsWith('/')
    ? basePathname.slice(0, -1)
    : basePathname;
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;

  if (!normalizedBase) {
    return normalizedPath;
  }

  return `${normalizedBase}${normalizedPath}`;
};
